import { useState, useMemo, useEffect } from 'react';
import { State } from 'country-state-city';
import { useTheme } from '@mui/material';

export type StateOption = {
  code: string;
  name: string;
  countryCode: string;
};

export type StateDropdownProps = {
  countryCode: string;
  initialStateCode?: string;
  onSelect: (state: string) => void;
  disabled?: boolean;
};

const useStateDropdown = ({
  countryCode,
  initialStateCode,
  onSelect,
}: StateDropdownProps) => {
  const { spacing } = useTheme();
  const [searchValue, setSearchValue] = useState('');

  const [selectedState, setSelectedState] = useState<StateOption | null>(null);

  const handleOptionSelect = (
    _event: unknown,
    newValue: StateOption | null,
  ) => {
    setSelectedState(newValue);
    onSelect(newValue?.code || '');
  };

  const allStates: StateOption[] = useMemo(() => {
    if (!countryCode) return [];

    try {
      const states = State.getStatesOfCountry(countryCode);
      return states.map((state) => ({
        code: state.isoCode,
        name: state.name,
        countryCode: state.countryCode,
      }));
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  }, [countryCode]);

  const filteredStates = useMemo(() => {
    if (!searchValue) return allStates;
    const searchLower = searchValue.toLowerCase();
    return allStates.filter((state) =>
      state.name.toLowerCase().includes(searchLower),
    );
  }, [searchValue, allStates]);

  const handleInputChange = (_event: unknown, newValue: string) => {
    setSearchValue(newValue);
  };

  useEffect(() => {
    setSelectedState(null);
    onSelect('');
  }, [countryCode, onSelect]);

  useEffect(() => {
    if (initialStateCode && filteredStates.length > 0) {
      const found = filteredStates.find((opt) => opt.code === initialStateCode);
      setSelectedState(found || null);
    }
  }, [initialStateCode, filteredStates]);

  return {
    options: filteredStates,
    handleInputChange,
    spacing,
    selectedState,
    handleOptionSelect,
    hasStates: allStates.length > 0,
  };
};

export default useStateDropdown;
